import { useCallback, useEffect, useMemo, useState } from "react";
import { Database, DownloadSimple, MagnifyingGlass, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const d10 = (v) => (v ? String(v).slice(0, 10) : "—");

const TABS = [
  {
    key: "branches", label: "Bank Branches", path: "/branches",
    cols: [
      { k: "bank_name", h: "Bank" }, { k: "branch_name", h: "Branch" },
      { k: "region", h: "Region" }, { k: "contact_person", h: "Contact" },
      { k: "phone", h: "Phone" }, { k: "stage", h: "Stage" },
    ],
  },
  {
    key: "clients", label: "Clients", path: "/clients",
    cols: [
      { k: "name", h: "Name" }, { k: "client_type", h: "Type" }, { k: "city", h: "City" },
      { k: "phone", h: "Phone" }, { k: "email", h: "Email" }, { k: "kyc_status", h: "KYC" },
    ],
  },
  {
    key: "jobs", label: "Valuation Jobs", path: "/jobs",
    cols: [
      { k: "reference", h: "Reference", mono: true }, { k: "applicant_name", h: "Applicant" },
      { k: "bank_name", h: "Bank" }, { k: "property_type", h: "Property" },
      { k: "status", h: "Status" }, { k: "deadline", h: "Deadline", fmt: d10 },
      { k: "fee", h: "Fee", fmt: inr, right: true },
    ],
  },
  {
    key: "invoices", label: "Invoices", path: "/invoices",
    cols: [
      { k: "invoice_number", h: "Invoice #", mono: true }, { k: "bill_to_name", h: "Billed To" },
      { k: "issue_date", h: "Issued", fmt: d10 }, { k: "due_date", h: "Due", fmt: d10 },
      { k: "status", h: "Status" }, { k: "total", h: "Total", fmt: inr, right: true },
    ],
  },
];

export default function Records() {
  const [tab, setTab] = useState("branches");
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const active = TABS.find((t) => t.key === tab);

  const load = useCallback(() => {
    setRows(null);
    api.get(active.path).then((r) => setRows(r.data)).catch((err) => {
      toast.error(formatApiError(err.response?.data?.detail));
      setRows([]);
    });
  }, [active.path]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !rows) return rows || [];
    return rows.filter((r) => active.cols.some((c) => String(r[c.k] ?? "").toLowerCase().includes(q)));
  }, [rows, search, active]);

  const downloadCsv = () => window.open(`${API}/export/${active.key}`, "_blank");
  const downloadBackup = async () => {
    try {
      const { data } = await api.get("/backup/all");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `aakriti-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Full backup downloaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="records-page">
      <PageHeader
        overline="Records" title="All Data Records"
        subtitle="Browse, search and export everything stored in your workspace"
        action={
          <Button variant="outline" onClick={downloadBackup} className="rounded-full" data-testid="records-backup-btn">
            <FileArrowDown size={16} className="mr-1" /> Full Backup (JSON)
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2" data-testid="records-tabs">
        {TABS.map((t) => (
          <button
            key={t.key} onClick={() => { setTab(t.key); setSearch(""); }}
            data-testid={`records-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-150 ${
              tab === t.key ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-border hover:bg-muted"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Records in this table" value={rows ? rows.length : "…"} testid="records-total" />
        <Stat label="Matching your search" value={rows ? filtered.length : "…"} tone="text-accent" testid="records-matching" />
        <div className="rounded-lg border border-border bg-white p-4 flex items-center">
          <Button onClick={downloadCsv} className="rounded-full w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="records-csv-btn">
            <DownloadSimple size={16} className="mr-1" /> Download {active.label} CSV
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${active.label.toLowerCase()}…`} className="pl-9" data-testid="records-search" />
      </div>

      {rows === null ? (
        <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading records…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Database}>No {active.label.toLowerCase()} found{search ? " for this search" : " yet"}.</EmptyState>
      ) : (
        <Card className="border-border overflow-x-auto">
          <Table data-testid="records-table">
            <TableHeader>
              <TableRow>
                {active.cols.map((c) => (
                  <TableHead key={c.k} className={c.right ? "text-right" : ""}>{c.h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`records-row-${r.id}`}>
                  {active.cols.map((c) => (
                    <TableCell key={c.k} className={`${c.right ? "text-right tabular" : ""} ${c.mono ? "font-mono text-xs" : ""}`}>
                      {c.fmt ? c.fmt(r[c.k]) : (r[c.k] ?? "—") || "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
